import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { deactivateUser, deleteUser, reactivateUser, restoreUser, updateUser } from '@mkcore/platform/schema';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ErrorState,
  Field,
  Input,
  Label,
  NativeSelect,
  PageHeader,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mkcore/ui';
import { ApiError } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { rolesQuery, userQuery } from '@/features/admin/queries';
import { AuditList } from '@/features/admin/audit-list';
import { GrantsPanel } from '@/features/admin/grants-panel';
import { TokensPanel } from '@/features/admin/tokens-panel';
import { ConfirmButton } from '@/components/confirm';

export const Route = createFileRoute('/_app/admin/users/$id')({ component: UserDetail });

function UserDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const me = useMe();
  const user = useQuery(userQuery(id));
  const roles = useQuery(rolesQuery);
  const canUpdate = useCan('admin:update');
  const canDelete = useCan('admin:delete');
  const canCreate = useCan('admin:create');
  const message = useErrorMessage();
  const [form, setForm] = useState({ name: '', email: '', entraTenantId: '', entraObjectId: '', locale: 'nl' as 'nl' | 'en' });
  const [roleKeys, setRoleKeys] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user.data) return;
    setForm({
      name: user.data.name,
      email: user.data.email ?? '',
      entraTenantId: user.data.entraTenantId ?? '',
      entraObjectId: user.data.entraObjectId ?? '',
      locale: user.data.locale,
    });
    setRoleKeys(user.data.roleKeys);
  }, [user.data]);

  const invalidate = [['admin', 'user'], ['admin', 'auditLog']];
  const save = useActionMutation(updateUser, { invalidate, success: t('common.saved'), silentErrors: true });
  const deactivate = useActionMutation(deactivateUser, { invalidate });
  const reactivate = useActionMutation(reactivateUser, { invalidate });
  const remove = useActionMutation(deleteUser, { invalidate });
  const restore = useActionMutation(restoreUser, { invalidate });

  if (user.isError) return <ErrorState title={t('errors.generic')} description={message(user.error)} />;
  if (!user.data) return <Skeleton className="h-96" />;
  const u = user.data;
  const human = u.kind === 'human';
  const field = (k: 'name' | 'email' | 'entraTenantId' | 'entraObjectId') => ({
    value: form[k],
    disabled: !canUpdate || !!u.deletedAt,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value }),
  });

  return (
    <>
      <Link to="/admin/users" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('users.title')}
      </Link>
      <PageHeader
        eyebrow={human ? t('users.kindHuman') : t('users.kindAgent')}
        title={u.name}
        description={u.email ?? undefined}
        actions={
          <>
            {u.deletedAt ? (
              <Badge tone="danger">{t('users.deleted')}</Badge>
            ) : (
              <Badge tone={u.status === 'active' ? 'success' : 'neutral'}>
                {u.status === 'active' ? t('users.statusActive') : t('users.statusDeactivated')}
              </Badge>
            )}
            {u.deletedAt
              ? canUpdate && (
                  <Button variant="secondary" loading={restore.isPending} onClick={() => restore.mutate({ input: { id } })}>
                    {t('common.restore')}
                  </Button>
                )
              : u.status === 'active'
                ? canDelete &&
                  u.id !== me.id && (
                    <ConfirmButton
                      label={t('users.deactivate')}
                      title={t('users.deactivateConfirm', { name: u.name })}
                      onConfirm={() => deactivate.mutateAsync({ input: { id } })}
                    />
                  )
                : canUpdate && (
                    <Button variant="secondary" loading={reactivate.isPending} onClick={() => reactivate.mutate({ input: { id } })}>
                      {t('users.reactivate')}
                    </Button>
                  )}
            {!u.deletedAt && canDelete && u.id !== me.id && (
              <ConfirmButton
                variant="danger"
                label={t('common.delete')}
                title={t('users.deleteConfirm', { name: u.name })}
                onConfirm={() => remove.mutateAsync({ input: { id }, ifMatch: u.version })}
              />
            )}
          </>
        }
      />
      <Tabs defaultValue="details">
        <TabsList className="mb-5">
          <TabsTrigger value="details">{t('common.details')}</TabsTrigger>
          <TabsTrigger value="access">{t('users.access')}</TabsTrigger>
          <TabsTrigger value="tokens">{t('users.tokens')}</TabsTrigger>
          <TabsTrigger value="history">{t('users.history')}</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <Card className="p-6">
            <form
              className="grid max-w-xl gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                setErrors({});
                save.mutate(
                  {
                    ifMatch: u.version,
                    input: {
                      id,
                      name: form.name,
                      locale: form.locale,
                      roleKeys,
                      ...(human && {
                        email: form.email || null,
                        entraTenantId: form.entraTenantId || null,
                        entraObjectId: form.entraObjectId || null,
                      }),
                    },
                  },
                  {
                    onError: (err) =>
                      setErrors(err instanceof ApiError ? { ...err.fieldErrors(), _: message(err) } : { _: message(err) }),
                  },
                );
              }}
            >
              <Field label={t('common.name')} error={errors.name}>
                {(p) => <Input {...p} required {...field('name')} />}
              </Field>
              {human && (
                <>
                  <Field label={t('common.email')} error={errors.email}>
                    {(p) => <Input {...p} type="email" {...field('email')} />}
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={t('users.entraTenantId')} error={errors.entraTenantId}>
                      {(p) => <Input {...p} className="font-mono text-xs" {...field('entraTenantId')} />}
                    </Field>
                    <Field label={t('users.entraObjectId')} error={errors.entraObjectId}>
                      {(p) => <Input {...p} className="font-mono text-xs" {...field('entraObjectId')} />}
                    </Field>
                  </div>
                </>
              )}
              <Field label={t('users.language')}>
                {(p) => (
                  <NativeSelect {...p} value={form.locale} disabled={!canUpdate} onChange={(e) => setForm({ ...form, locale: e.target.value as 'nl' | 'en' })}>
                    <option value="nl">Nederlands</option>
                    <option value="en">English</option>
                  </NativeSelect>
                )}
              </Field>
              <fieldset className="grid gap-2" disabled={!canUpdate}>
                <Label asChild>
                  <legend>{t('users.roles')}</legend>
                </Label>
                <div className="flex flex-wrap gap-3">
                  {roles.data?.items.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={roleKeys.includes(r.key)}
                        disabled={!canUpdate}
                        onCheckedChange={(v) => setRoleKeys(v === true ? [...roleKeys, r.key] : roleKeys.filter((x) => x !== r.key))}
                      />
                      {r.name}
                    </label>
                  ))}
                </div>
              </fieldset>
              {errors._ && (
                <p role="alert" className="text-sm text-danger">
                  {errors._}
                </p>
              )}
              {canUpdate && !u.deletedAt && (
                <div>
                  <Button type="submit" loading={save.isPending}>
                    {t('common.save')}
                  </Button>
                </div>
              )}
            </form>
          </Card>
        </TabsContent>
        <TabsContent value="access">
          <GrantsPanel principalId={id} canManage={canCreate && !u.deletedAt} />
        </TabsContent>
        <TabsContent value="tokens">
          {human && !u.linked ? <p className="text-sm text-fg-2">{t('tokens.notLinked')}</p> : <TokensPanel principalId={id} canCreate={canCreate && u.status === 'active'} />}
        </TabsContent>
        <TabsContent value="history">
          <AuditList filter={{ entityType: 'user', entityId: id }} compact />
        </TabsContent>
      </Tabs>
    </>
  );
}
